import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { PageHero } from "./PageHero";
import { ASSETS } from "@/lib/assets";
import { CLEARBOOK_BOOKING_URL } from "@/lib/clearbook";

export const ServicePageTemplate = ({
  eyebrow,
  title,
  subtitle,
  heroImage,
  intro,
  bullets = [],
  process = [],
  related = [],
  ctaHeading,
  ctaBody,
}) => {
  return (
    <>
      <PageHero
        eyebrow={eyebrow}
        title={title}
        subtitle={subtitle}
        image={heroImage}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="font-display text-2xl md:text-3xl leading-snug text-white/90 italic">
              {intro}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="eyebrow mb-6">Det får du</div>
            <ul className="space-y-4">
              {bullets.map((b, i) => (
                <li key={i} className="flex gap-3 text-white/80 font-light">
                  <Check className="w-5 h-5 flex-shrink-0 mt-1 text-[color:var(--fk-gold)]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {process.length > 0 && (
        <section className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
              <div className="md:col-span-4">
                <div className="eyebrow mb-4">Processen</div>
                <h2 className="font-display text-4xl md:text-5xl leading-tight">
                  Sådan <em className="text-[color:var(--fk-gold)]">arbejder vi</em>
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 border-t border-white/10">
              {process.map((p, i) => (
                <div
                  key={i}
                  className="p-8 border-r border-b border-white/10 last:border-r-0"
                >
                  <div className="font-display text-3xl text-[color:var(--fk-gold)] mb-4">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-xl mb-3">{p.title}</h3>
                  <p className="text-sm text-white/60 font-light">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-white/10 bg-[color:var(--fk-bg-2)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow mb-4">Klar når du er</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">
                {ctaHeading || <>Brug for hjælp <em className="text-[color:var(--fk-gold)]">nu?</em></>}
              </h2>
              <p className="mt-6 text-white/60 font-light max-w-xl">
                {ctaBody || "Vores døgnvagt står klar. Ring og få fat i en autoriseret kloakmester med det samme — eller book online."}
              </p>
            </div>
            <div className="md:col-span-5 flex flex-col sm:flex-row gap-4">
              <a
                href={ASSETS.brand.phoneTel}
                className="px-6 py-4 text-[11px] tracking-[0.24em] uppercase bg-[color:var(--fk-gold)] text-black hover:bg-[color:var(--fk-gold-hi)] transition-colors text-center"
              >
                {ASSETS.brand.phone}
              </a>
              <a
                href={CLEARBOOK_BOOKING_URL}
                className="px-6 py-4 text-[11px] tracking-[0.24em] uppercase border border-white/20 hover:border-[color:var(--fk-gold)] hover:text-[color:var(--fk-gold)] transition-colors text-center"
              >
                Book online
              </a>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
            <div className="eyebrow mb-8">Andre ydelser</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
              {related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="group p-8 bg-[color:var(--fk-bg)] hover:bg-[color:var(--fk-bg-2)] transition-colors flex items-center justify-between"
                >
                  <span className="font-display text-lg">{r.label}</span>
                  <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[color:var(--fk-gold)] group-hover:rotate-12 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};
